const selectors$e = {
    animation: '[data-animation]',
    apiContent: '[data-api-content]',
    buttonQuickAdd: '[data-quick-add-btn]',
    buttonAddToCart: '[data-add-to-cart]',
    cartDrawer: 'cart-drawer',
    cartPage: '[data-cart-page]',
    cartLineItems: '[data-line-items]',
    dialog: 'dialog',
    focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
    messageError: '[data-message-error]',
    modalButton: '[data-quick-add-modal-handle]',
    modalContainer: '[data-product-upsell-container]',
    modalContent: '[data-product-upsell-ajax]',
    modalClose: '[data-quick-add-modal-close]',
    productGridItem: 'data-grid-item',
    productInformationHolder: '[data-product-information]',
    quickAddHolder: '[data-quick-add-holder]',
    quickAddModal: '[data-quick-add-modal]',
    quickAddModalTemplate: '[data-quick-add-modal-template]',
    tooltip: '[data-tooltip]',
};

const classes$9 = {
    added: 'is-added',
    animated: 'is-animated',
    disabled: 'is-disabled',
    error: 'has-error',
    loading: 'is-loading',
    open: 'is-open',
    overlayText: 'product-item--overlay-text',
    visible: 'is-visible',
    siblingLinkCurrent: 'sibling__link--current',
};

const attributes$a = {
    closing: 'closing',
    productId: 'data-product-id',
    modalHandle: 'data-quick-add-modal-handle',
    modalRedirectHandle: 'data-quick-add-redirect-url',
    siblingSwapper: 'data-sibling-swapper',
    quickAddHolder: 'data-quick-add-holder',
};

class QuickAddProduct extends HTMLElement {
    constructor() {
        super();

        this.container = this;
        this.quickAddHolder = this.container.querySelector(selectors$e.quickAddHolder);
        if (this.quickAddHolder) {
            this.modal = null;
            this.currentModal = null;
            this.productId = this.quickAddHolder.getAttribute(attributes$a.quickAddHolder);
            this.modalButton = this.quickAddHolder.querySelector(selectors$e.modalButton);
            this.handle = this.modalButton?.getAttribute(attributes$a.modalHandle);
            this.redirectHandle = this.quickAddHolder?.getAttribute(attributes$a.modalRedirectHandle);
            this.buttonQuickAdd = this.quickAddHolder.querySelector(selectors$e.buttonQuickAdd);
            this.buttonATC = this.quickAddHolder.querySelector(selectors$e.buttonAddToCart);
            this.button = this.modalButton || this.buttonATC;
            this.modalClose = this.modalClose.bind(this);
            this.modalCloseOnProductAdded = this.modalCloseOnProductAdded.bind(this);
            this.isAnimating = false;

            this.modalButtonClickEvent = this.modalButtonClickEvent.bind(this);
            this.quickAddLoadingToggle = this.quickAddLoadingToggle.bind(this);
        }
    }

    connectedCallback() {
        /**
         * Modal button works for multiple variants products
         */
        if (this.modalButton) {
            this.modalButton.addEventListener('click', this.modalButtonClickEvent);
        }

        /**
         * Quick add button works for single variant products
         */
        if (this.buttonATC) {
            this.buttonATC.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleQuickAddFormSubmit(e);
            });
        }

        if (this.quickAddHolder) {
            this.quickAddHolder.addEventListener('animationend', this.quickAddLoadingToggle);
            this.errorHandler();
        }
    }

    modalButtonClickEvent(e) {
        e.preventDefault();
        const isSiblingSwapper = this.modalButton.hasAttribute(attributes$a.siblingSwapper);
        const isSiblingLinkCurrent = this.modalButton.classList.contains(classes$9.siblingLinkCurrent);

        if (isSiblingLinkCurrent) return;

        this.modalButton.classList.add(classes$9.loading);
        this.modalButton.disabled = true;

        // Siblings product modal swapper
        if (isSiblingSwapper && !isSiblingLinkCurrent) {
            this.currentModal = e.target.closest(selectors$e.quickAddModal);
            this.currentModal.classList.add(classes$9.loading);
        }

        this.renderModal();
    }

    modalCreate(response) {
        const cachedModal = document.querySelector(`${selectors$e.quickAddModal}[${attributes$a.productId}="${this.productId}"]`);

        if (cachedModal) {
            this.modal = cachedModal;
            this.modalOpen();
        } else {
            const modalTemplate = this.quickAddHolder.querySelector(selectors$e.quickAddModalTemplate);
            if (!modalTemplate) return;

            const htmlObject = document.createElement('div');
            htmlObject.innerHTML = modalTemplate.innerHTML;

            // Add dialog to the body
            document.body.appendChild(htmlObject.querySelector(selectors$e.quickAddModal));
            modalTemplate.remove();

            this.modal = document.querySelector(`${selectors$e.quickAddModal}[${attributes$a.productId}="${this.productId}"]`);

            // Parse the full product page HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(response, 'text/html');
            
            // Extract the product-main section from the page
            const productMainSection = doc.querySelector('.section-product-main') || 
                                      doc.querySelector('[class*="product-main"]') ||
                                      doc.querySelector('#MainContent .product-main');
            
            if (productMainSection) {
                this.modal.querySelector(selectors$e.modalContent).innerHTML = productMainSection.innerHTML;
            } else {
                console.error('Could not find product-main section in page');
            }

            this.modalCreatedCallback();
        }
    }

    modalOpen() {
        if (this.currentModal) {
            this.currentModal.dispatchEvent(new CustomEvent('theme:modal:close', {bubbles: false}));
        }

        // Check if browser supports Dialog tags
        if (typeof this.modal.show === 'function') {
            this.modal.show();
        }
        this.modal.setAttribute('open', true);
        this.modal.removeAttribute('inert');

        // add overflow hidden to prevent body scroll
        document.body.style.overflow = 'hidden';

        this.quickAddHolder.classList.add(classes$9.disabled);

        if (this.modalButton) {
            this.modalButton.classList.remove(classes$9.loading);
            this.modalButton.disabled = false;
        }

        // Animate items
        requestAnimationFrame(() => {
            this.modal.querySelectorAll(selectors$e.animation).forEach((item) => {
                item.classList.add(classes$9.animated);
            });
        });

        document.dispatchEvent(new CustomEvent('theme:quick-add:open', {bubbles: true}));
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
        document.addEventListener('theme:product:added', this.modalCloseOnProductAdded, {once: true});
    }

    modalClose() {
        if (this.isAnimating) {
            return;
        }

        if (!this.modal.hasAttribute(attributes$a.closing)) {
            this.modal.setAttribute(attributes$a.closing, '');
            this.isAnimating = true;
            return;
        }

        // remove overflow hidden to allow body scroll
        document.body.style.overflow = '';

        // Check if browser supports Dialog tags
        if (typeof this.modal.close === 'function') {
            this.modal.close();
        } else {
            this.modal.removeAttribute('open');
        }

        this.modal.removeAttribute(attributes$a.closing);
        this.modal.setAttribute('inert', '');
        this.modal.classList.remove(classes$9.loading);

        if (this.modalButton) {
            this.modalButton.disabled = false;
        }

        if (this.quickAddHolder && this.quickAddHolder.classList.contains(classes$9.disabled)) {
            this.quickAddHolder.classList.remove(classes$9.disabled);
        }

        this.resetAnimatedItems();
        document.dispatchEvent(new CustomEvent('theme:modal:close', {bubbles: false}));

        // Unlock scroll if no other drawers & modals are open
        document.removeEventListener('theme:product:added', this.modalCloseOnProductAdded);
    }

    modalEvents() {
        // Close button click event
        this.modal.querySelector(selectors$e.modalClose)?.addEventListener('click', (e) => {
            e.preventDefault();
            this.modalClose();
        });

        // Close dialog on click outside content
        this.modal.addEventListener('click', (event) => {
            if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
                this.modalClose();
            }
        });

        // Close dialog on click ESC key pressed
        this.modal.addEventListener('keydown', (event) => {
            if (event.code == 'Escape') {
                event.preventDefault();
                this.modalClose();
            }
        });

        this.modal.addEventListener('theme:modal:close', () => {
            this.modalClose();
        });

        // Close dialog after animation completes
        this.modal.addEventListener('animationend', (event) => {
            // if (event.target !== this.modal) return;
            this.isAnimating = false;

            if (this.modal.hasAttribute(attributes$a.closing)) {
                this.modalClose();
            }
        });
    }

    modalCloseOnProductAdded() {
        this.resetQuickAddButtons();
        if (this.modal && this.modal.hasAttribute('open')) {
            this.modalClose();
        }
    }

    quickAddLoadingToggle(e) {
        if (e.target != this.quickAddHolder) return;
        this.quickAddHolder.classList.remove(classes$9.disabled);
    }

    /**
     * Handle error cart response
     */
    errorHandler() {
        this.quickAddHolder.addEventListener('theme:cart:error', (event) => {
            const holder = event.detail.holder;
            const parentProduct = holder.closest(`[${selectors$e.productGridItem}]`);
            if (!parentProduct) return;

            const errorMessageHolder = holder.querySelector(selectors$e.messageError);
            const hasOverlayText = parentProduct.classList.contains(classes$9.overlayText);
            const productInfo = parentProduct.querySelector(selectors$e.productInformationHolder);
            const button = holder.querySelector(selectors$e.buttonAddToCart);

            if (button) {
                button.classList.remove(classes$9.added, classes$9.loading);
                holder.classList.add(classes$9.error);
            }

            if (errorMessageHolder) {
                errorMessageHolder.innerText = event.detail.description;
            }

            if (hasOverlayText) {
                productInfo.classList.add(classes$9.hidden);
            }

            setTimeout(() => {
                this.resetQuickAddButtons();

                if (hasOverlayText) {
                    productInfo.classList.remove(classes$9.hidden);
                }
            }, 300);
        });
    }

    /**
     * Reset buttons to default states
     */
    resetQuickAddButtons() {
        if (this.quickAddHolder) {
            this.quickAddHolder.classList.remove(classes$9.visible, classes$9.error);
        }

        if (this.buttonQuickAdd) {
            this.buttonQuickAdd.classList.remove(classes$9.added);
            this.buttonQuickAdd.disabled = false;
        }
    }

    renderModal() {
        if (this.modal) {
            this.modalOpen();
        } else {
            // Check if we have a prefetched version
            const prefetchedContent = window.QuickAddPrefetch?.get?.(this.handle);
            if (prefetchedContent) {
                this.modalCreate(prefetchedContent);
            } else {
                // Fetch full product page and extract section
                fetch(`/products/${this.handle}`)
                    .then(this.upsellErrorsHandler)
                    .then((response) => {
                        return response.text();
                    })
                    .then((response) => {
                        this.modalCreate(response);
                    });
            }
        }
    }

    modalCreatedCallback() {
        this.modalEvents();
        this.modalOpen();
    }

    upsellErrorsHandler(response) {
        if (!response.ok) {
            return response.json().then(function (json) {
                const e = new FetchError({
                    status: response.statusText,
                    headers: response.headers,
                    json: json,
                });
                throw e;
            });
        }
        return response;
    }

    resetAnimatedItems() {
        this.modal?.querySelectorAll(selectors$e.animation).forEach((item) => {
            item.classList.remove(classes$9.animated);
        });
    }

    handleQuickAddFormSubmit(event) {
        event.preventDefault();

        const form = event.target.closest('form')
        const formData = new FormData(form);
        const submitButton = form.querySelector('[type="submit"]');
        const productId = formData.get('id');

        if (!productId) {
            console.error('No product ID found in form');
            return;
        }

        // Add sections to update in response
        if (window.Shopify && window.Shopify.routes) {
            formData.append('sections', 'cart-icon-bubble,cart-drawer');
            formData.append('sections_url', window.location.pathname);
        }

        // Show loading state
        if (submitButton) {
            submitButton.classList.add('is-loading');
            submitButton.setAttribute('aria-disabled', true);
        }

        // Request configuration
        const config = {
            method: 'POST',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
        };

        // Send the request
        fetch(window.Shopify.routes.cart_add_url || '/cart/add.js', config)
            .then(response => response.json())
            .then(response => {
                if (response.status) {
                    throw new Error(response.description || 'Error adding item to cart');
                }

                // Update cart UI
                updateCartUI(response);

                // Dispatch product added event
                document.dispatchEvent(
                    new CustomEvent('theme:product:added', {
                        bubbles: true,
                        detail: { response }
                    })
                );

                return response;
            })
            .catch(error => {
                console.error('Error adding to cart:', error);

                // Dispatch error event for error handling
                form.closest('quick-add-product')?.dispatchEvent(
                    new CustomEvent('theme:cart:error', {
                        bubbles: true,
                        detail: {
                            description: error.message || 'Could not add item to cart',
                            holder: form.closest('[data-quick-add-holder]')
                        }
                    })
                );
            })
            .finally(() => {
                // Reset button state
                if (submitButton) {
                    submitButton.classList.remove('is-loading');
                    submitButton.removeAttribute('aria-disabled');
                }
            });
    }

}

if (!customElements.get('quick-add-product')) {
    customElements.define('quick-add-product', QuickAddProduct);
}

/**
 * QuickAddPrefetch - Prefetches product-main sections for quick add
 * This improves perceived performance by loading content before user clicks
 */
class QuickAddPrefetch {
    constructor() {
        this.cache = new Map();
        this.prefetchQueue = [];
        this.isPrefetching = false;
        this.maxConcurrent = 3; // Limit concurrent requests
        this.activeFetches = 0;
    }

    /**
     * Initialize prefetching for all products on the page
     */
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.startPrefetch());
        } else {
            this.startPrefetch();
        }
    }

    /**
     * Start prefetching products
     */
    startPrefetch() {
        // Find all quick add buttons on the page
        const quickAddButtons = document.querySelectorAll('[data-quick-add-modal-handle]');
        
        quickAddButtons.forEach((button, index) => {
            const handle = button.getAttribute('data-quick-add-modal-handle');
            if (handle && !this.cache.has(handle)) {
                // Add to queue with priority (visible items first)
                this.prefetchQueue.push({
                    handle,
                    priority: this.isInViewport(button) ? 0 : index
                });
            }
        });

        // Sort by priority
        this.prefetchQueue.sort((a, b) => a.priority - b.priority);

        // Start prefetching
        this.processPrefetchQueue();
    }

    /**
     * Process the prefetch queue
     */
    processPrefetchQueue() {
        if (this.isPrefetching || this.prefetchQueue.length === 0) {
            return;
        }

        this.isPrefetching = true;

        // Process multiple requests concurrently
        while (this.activeFetches < this.maxConcurrent && this.prefetchQueue.length > 0) {
            const item = this.prefetchQueue.shift();
            this.prefetchProduct(item.handle);
        }

        this.isPrefetching = false;
    }

    /**
     * Prefetch a single product
     */
    async prefetchProduct(handle) {
        if (this.cache.has(handle)) {
            return;
        }

        this.activeFetches++;

        try {
            // Fetch full product page instead of section API
            const response = await fetch(`/products/${handle}`, {
                priority: 'low', // Use low priority to not interfere with critical resources
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (response.ok) {
                const html = await response.text();
                this.cache.set(handle, html);
            }
        } catch (error) {
            console.warn(`Failed to prefetch product: ${handle}`, error);
        } finally {
            this.activeFetches--;
            
            // Continue processing queue if there are more items
            if (this.prefetchQueue.length > 0 && this.activeFetches < this.maxConcurrent) {
                setTimeout(() => this.processPrefetchQueue(), 100);
            }
        }
    }

    /**
     * Get cached content
     */
    get(handle) {
        return this.cache.get(handle);
    }

    /**
     * Check if element is in viewport
     */
    isInViewport(element) {
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    /**
     * Clear cache
     */
    clear() {
        this.cache.clear();
    }
}

// Create a singleton instance
const prefetchInstance = new QuickAddPrefetch();

// Export for use in QuickAddProduct
window.QuickAddPrefetch = prefetchInstance;

// Initialize prefetching when on collection pages
if (document.body.classList.contains('template-collection') || 
    document.querySelector('[data-section-type="collection"]')) {
    prefetchInstance.init();
}

/**
 * InlineBuyBox - Fetches and embeds purchase options directly in collection cards
 * Provides a more complete buying experience without modal interaction
 */
class InlineBuyBox {
    constructor() {
        this.containers = [];
        this.cache = new Map();
    }

    /**
     * Initialize inline buy boxes
     */
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.loadBuyBoxes());
        } else {
            this.loadBuyBoxes();
        }
    }

    /**
     * Load all inline buy boxes on the page
     */
    loadBuyBoxes() {
        const inlineBuyBoxes = document.querySelectorAll('[data-inline-buy-box]');
        
        if (inlineBuyBoxes.length === 0) {
            return;
        }

        inlineBuyBoxes.forEach((container) => {
            const handle = container.getAttribute('data-product-handle');
            const productId = container.getAttribute('data-product-id');
            
            if (handle) {
                this.fetchAndRenderBuyBox(container, handle, productId);
            }
        });
    }

    /**
     * Fetch product page and extract purchase options
     */
    async fetchAndRenderBuyBox(container, handle, productId) {
        try {
            // Check cache first
            if (this.cache.has(handle)) {
                this.renderBuyBox(container, this.cache.get(handle), productId);
                return;
            }

            // Fetch product page
            const response = await fetch(`/products/${handle}`, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            
            // Parse the HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Find the element with ID containing "purchase_options"
            const purchaseOptionsBlock = doc.querySelector('[id*="purchase_options"]');
            
            if (!purchaseOptionsBlock) {
                console.warn(`No purchase options block found for product: ${handle}`);
                container.innerHTML = '<p style="text-align: center; padding: 1rem;">Purchase options not available</p>';
                return;
            }

            // Cache the content
            this.cache.set(handle, purchaseOptionsBlock.outerHTML);
            
            // Render the buy box
            this.renderBuyBox(container, purchaseOptionsBlock.outerHTML, productId);
            
        } catch (error) {
            console.error(`Failed to load buy box for product: ${handle}`, error);
            container.innerHTML = '<p style="text-align: center; padding: 1rem;">Failed to load purchase options</p>';
        }
    }

    /**
     * Render the buy box content
     */
    renderBuyBox(container, content, productId) {
        // Replace loader with content
        container.innerHTML = content;
        
        // Dispatch event for any scripts that need to reinitialize
        container.dispatchEvent(new CustomEvent('inline-buy-box:loaded', {
            bubbles: true,
            detail: { productId, container }
        }));
        
        // Re-initialize any custom elements within the buy box
        this.initializeCustomElements(container);
    }

    /**
     * Initialize custom elements within the container
     */
    initializeCustomElements(container) {
        // Find and initialize any custom elements that might need it
        const customElements = container.querySelectorAll('[is], purchase-options, product-form');
        customElements.forEach((element) => {
            // Trigger connectedCallback by removing and re-adding if needed
            if (element.connectedCallback && typeof element.connectedCallback === 'function') {
                element.connectedCallback();
            }
        });
    }

    /**
     * Clear cache
     */
    clearCache() {
        this.cache.clear();
    }
}

// Create singleton instance
const inlineBuyBoxInstance = new InlineBuyBox();
window.InlineBuyBox = inlineBuyBoxInstance;

// Initialize inline buy boxes when on collection pages
if (document.body.classList.contains('template-collection') || 
    document.querySelector('[data-section-type="collection"]')) {
    inlineBuyBoxInstance.init();
}
